/**
 * Korean country display name -> flag emoji, independent of the backend's
 * own `flagCode` field (confirmed null in every captured navigation sample
 * — see SportsnewContext.jsx's leagueCountryMap). An emoji flag is just two
 * Unicode regional-indicator symbols computed from an ISO 3166-1 alpha-2
 * code, so once we know the code we never need an image asset or a
 * third-party CDN.
 *
 * Entries like "국제" (International) aren't real countries — they represent
 * multi-country/continental competitions, which get a globe fallback
 * instead of guessing at a flag (matches reference sportsbook UIs, which
 * show a 🌍-style icon for e.g. "INT CF" alongside real country flags for
 * domestic leagues).
 */

const GLOBE_FALLBACK = '🌍';

const normalizeCountryName = (value) => String(value ?? '').trim();

/** Korean country display name -> ISO 3166-1 alpha-2 code. Covers the
 * countries most likely to appear in football/major-sport fixture lists;
 * an unmapped name falls back to the globe rather than showing nothing. */
const KOREAN_COUNTRY_TO_ISO = {
  '대한민국': 'kr', '한국': 'kr',
  '일본': 'jp',
  '중국': 'cn',
  '북한': 'kp',
  '대만': 'tw',
  '홍콩': 'hk',
  '몽골': 'mn',
  '베트남': 'vn',
  '태국': 'th',
  '필리핀': 'ph',
  '인도네시아': 'id',
  '말레이시아': 'my',
  '싱가포르': 'sg',
  '미얀마': 'mm',
  '캄보디아': 'kh',
  '라오스': 'la',
  '인도': 'in',
  '파키스탄': 'pk',
  '방글라데시': 'bd',
  '스리랑카': 'lk',
  '네팔': 'np',
  '우즈베키스탄': 'uz',
  '카자흐스탄': 'kz',
  '타지키스탄': 'tj',
  '투르크메니스탄': 'tm',
  '키르기스스탄': 'kg',

  '영국': 'gb', '잉글랜드': 'gb-eng', '스코틀랜드': 'gb-sct', '웨일스': 'gb-wls', '북아일랜드': 'gb-nir',
  '아일랜드': 'ie',
  '프랑스': 'fr',
  '독일': 'de',
  '스페인': 'es',
  '이탈리아': 'it',
  '포르투갈': 'pt',
  '네덜란드': 'nl',
  '벨기에': 'be',
  '스위스': 'ch',
  '오스트리아': 'at',
  '그리스': 'gr',
  '터키': 'tr', '튀르키예': 'tr',
  '폴란드': 'pl',
  '체코': 'cz',
  '슬로바키아': 'sk',
  '헝가리': 'hu',
  '루마니아': 'ro',
  '불가리아': 'bg',
  '세르비아': 'rs',
  '크로아티아': 'hr',
  '보스니아': 'ba', '보스니아 헤르체고비나': 'ba',
  '슬로베니아': 'si',
  '몬테네그로': 'me',
  '북마케도니아': 'mk', '마케도니아': 'mk',
  '알바니아': 'al',
  '코소보': 'xk',
  '우크라이나': 'ua',
  '벨라루스': 'by',
  '러시아': 'ru',
  '몰도바': 'md',
  '리투아니아': 'lt',
  '라트비아': 'lv',
  '에스토니아': 'ee',
  '핀란드': 'fi',
  '스웨덴': 'se',
  '노르웨이': 'no',
  '덴마크': 'dk',
  '아이슬란드': 'is',
  '몰타': 'mt',
  '키프로스': 'cy',
  '룩셈부르크': 'lu',
  '안도라': 'ad',
  '산마리노': 'sm',
  '리히텐슈타인': 'li',
  '지브롤터': 'gi',
  '조지아': 'ge',
  '아르메니아': 'am',
  '아제르바이잔': 'az',

  '미국': 'us',
  '캐나다': 'ca',
  '멕시코': 'mx',
  '브라질': 'br',
  '아르헨티나': 'ar',
  '우루과이': 'uy',
  '파라과이': 'py',
  '칠레': 'cl',
  '콜롬비아': 'co',
  '페루': 'pe',
  '에콰도르': 'ec',
  '볼리비아': 'bo',
  '베네수엘라': 've',
  '코스타리카': 'cr',
  '파나마': 'pa',
  '온두라스': 'hn',
  '과테말라': 'gt',
  '자메이카': 'jm',

  '호주': 'au', '오스트레일리아': 'au',
  '뉴질랜드': 'nz',

  '사우디아라비아': 'sa',
  '아랍에미리트': 'ae', 'UAE': 'ae',
  '카타르': 'qa',
  '쿠웨이트': 'kw',
  '바레인': 'bh',
  '오만': 'om',
  '이라크': 'iq',
  '이란': 'ir',
  '이스라엘': 'il',
  '요르단': 'jo',
  '레바논': 'lb',
  '시리아': 'sy',
  '예멘': 'ye',
  '팔레스타인': 'ps',

  '이집트': 'eg',
  '모로코': 'ma',
  '알제리': 'dz',
  '튀니지': 'tn',
  '리비아': 'ly',
  '나이지리아': 'ng',
  '가나': 'gh',
  '카메룬': 'cm',
  '세네갈': 'sn',
  '코트디부아르': 'ci',
  '남아프리카공화국': 'za', '남아공': 'za',
  '케냐': 'ke',
  '에티오피아': 'et',
  '탄자니아': 'tz',
  '우간다': 'ug',
  '잠비아': 'zm',
  '짐바브웨': 'zw',
  '콩고민주공화국': 'cd',
};

/** Unicode regional-indicator flag from an ISO 3166-1 alpha-2 code. Codes
 * with a region suffix (e.g. "gb-eng") aren't valid ISO alpha-2 — those
 * fall back to their base country's flag (gb) since there's no dedicated
 * England/Scotland/Wales emoji flag in Unicode. */
const flagEmojiFromIsoCode = (code) => {
  const base = code.split('-')[0];
  if (base.length !== 2) return '';
  const codePoints = [...base.toUpperCase()].map((c) => 127397 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

/** Resolve a flag emoji for a country's Korean display name — 🌍 for
 * International/continental entries or anything not in the mapping table,
 * never a blank/missing icon. */
export const countryFlagEmoji = (countryName) => {
  const name = normalizeCountryName(countryName);
  if (!name) return GLOBE_FALLBACK;
  const iso = KOREAN_COUNTRY_TO_ISO[name];
  if (!iso) return GLOBE_FALLBACK;
  const emoji = flagEmojiFromIsoCode(iso);
  return emoji || GLOBE_FALLBACK;
};

const firstNonEmpty = (...values) => values.map(normalizeCountryName).find(Boolean) || '';

const inferCountryFromLeagueName = (leagueName) => {
  const name = normalizeCountryName(leagueName);
  if (!name) return '';
  const prefix = name.match(/^(.+?)\s*[-|]\s*.+$/)?.[1]?.trim();
  if (prefix && (KOREAN_COUNTRY_TO_ISO[prefix] || prefix === '국제')) return prefix;
  if (/\b(UEFA|FIFA|AFC|CAF|CONCACAF|CONMEBOL|OFC)\b/i.test(name)) return '국제';
  if (/월드컵|유로|챔피언스리그|국제/i.test(name)) return '국제';
  return '';
};

export const resolveSportsnewCountryName = (fixture, leagueCountryMap = {}) => {
  if (!fixture) return '';
  const leagueId = fixture.leagueId ?? fixture.league_id ?? fixture.league?.leagueId ?? fixture.league?.id;
  const mappedCountry = leagueId != null ? leagueCountryMap[leagueId] : '';
  return firstNonEmpty(
    mappedCountry,
    fixture.countryName,
    fixture.countryNameKo,
    fixture.countryDisplayName,
    fixture.country,
    fixture.countryKo,
    fixture.country_name,
    fixture.country_name_ko,
    fixture.leagueCountryName,
    fixture.leagueCountryNameKo,
    fixture.league?.countryName,
    fixture.league?.countryNameKo,
    fixture.league?.country,
    fixture.league?.countryKo,
    fixture.league?.countryInfo?.displayName,
    fixture.league?.countryInfo?.name_ko,
    fixture.league?.countryInfo?.name,
    inferCountryFromLeagueName(fixture.leagueName ?? fixture.leagueNameKo ?? fixture.league?.name ?? fixture.league?.displayName)
  );
};
